import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecipes, useCreateRecipe, useUpdateRecipe, useDeleteRecipe } from '@/hooks/useRecipes';
import { useProducts } from '@/hooks/useProducts';
import { useToast } from '@/components/ui/ToastProvider';
import { requireAuth } from '@/lib/auth/routeGuards';
import type { Recipe, RecipeItemInput, Product } from '@/types/api';

type RecipesSearch = { page?: number };

export const Route = createFileRoute('/recipes/')({
  beforeLoad: requireAuth,
  component: RecipesPage,
  validateSearch: (search: Record<string, unknown>): RecipesSearch => ({
    page: Number(search.page) || 1,
  }),
});

interface EditingItem {
  productId: number;
  productName: string;
  productBrand?: string;
  quantityG: number;
  energyKcal100g?: number;
  proteins100g?: number;
  carbohydrates100g?: number;
  fat100g?: number;
  fiber100g?: number;
}

function calcNutrition(items: EditingItem[]) {
  return items.reduce(
    (acc, item) => {
      const ratio = item.quantityG / 100;
      return {
        energyKcal: acc.energyKcal + (item.energyKcal100g ?? 0) * ratio,
        proteins: acc.proteins + (item.proteins100g ?? 0) * ratio,
        carbohydrates: acc.carbohydrates + (item.carbohydrates100g ?? 0) * ratio,
        fat: acc.fat + (item.fat100g ?? 0) * ratio,
        fiber: acc.fiber + (item.fiber100g ?? 0) * ratio,
      };
    },
    { energyKcal: 0, proteins: 0, carbohydrates: 0, fat: 0, fiber: 0 },
  );
}

function NutritionRow({ label, value, unit }: { label: string; value: number; unit: string }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-gray-600">{label}</span>
      <span className="font-medium">{Math.round(value * 10) / 10}{unit}</span>
    </div>
  );
}

function RecipeCard({ recipe, onEdit, onDelete }: {
  recipe: Recipe;
  onEdit: (r: Recipe) => void;
  onDelete: (id: number) => void;
}) {
  const { t } = useTranslation();
  const ps = recipe.perServing;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900">{recipe.name}</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {recipe.items.length} {t('recipes.items').toLowerCase()} · {recipe.servings} {t('recipes.servings').toLowerCase()}
          </p>
          <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-500">
            <span>{Math.round(ps.energyKcal)} kcal</span>
            <span>P {Math.round(ps.proteins * 10) / 10}g</span>
            <span>G {Math.round(ps.carbohydrates * 10) / 10}g</span>
            <span>L {Math.round(ps.fat * 10) / 10}g</span>
            <span>F {Math.round(ps.fiber * 10) / 10}g</span>
            <span className="text-gray-400">/ {t('recipes.perServing').toLowerCase()}</span>
          </div>
        </div>
        <div className="flex gap-2 flex-shrink-0 ml-3">
          <button
            type="button"
            onClick={() => onEdit(recipe)}
            className="text-gray-400 hover:text-primary-600"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => onDelete(recipe.id)}
            className="text-gray-400 hover:text-red-500"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

function ProductPicker({ onSelect, exclude }: {
  onSelect: (p: Product) => void;
  exclude: number[];
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const { data } = useProducts({ query: query || undefined, limit: 50 });
  const products = (data?.products ?? []).filter((p) => !exclude.includes(p.id));

  return (
    <div>
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t('recipes.searchProduct')}
        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
      />
      {products.length > 0 && (
        <div className="mt-1 max-h-40 overflow-y-auto border border-gray-200 rounded-md">
          {products.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => { onSelect(p); setQuery(''); }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-0"
            >
              <span className="font-medium">{p.name}</span>
              {p.brand && <span className="text-gray-500 ml-1">— {p.brand}</span>}
            </button>
          ))}
        </div>
      )}
      {query && products.length === 0 && (
        <p className="mt-1 text-xs text-gray-400">{t('recipes.noProducts')}</p>
      )}
    </div>
  );
}

function RecipeForm({ recipe, onSave, onCancel }: {
  recipe?: Recipe;
  onSave: (data: { name: string; servings: number; items: RecipeItemInput[] }) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();

  const [name, setName] = useState(recipe?.name ?? '');
  const [servings, setServings] = useState(recipe?.servings ?? 2);
  const [items, setItems] = useState<EditingItem[]>(
    recipe?.items.map((i) => ({
      productId: i.productId,
      productName: i.productName,
      productBrand: i.productBrand,
      quantityG: i.quantityG,
      energyKcal100g: i.energyKcal != null ? (i.energyKcal / i.quantityG) * 100 : undefined,
      proteins100g: i.proteins != null ? (i.proteins / i.quantityG) * 100 : undefined,
      carbohydrates100g: i.carbohydrates != null ? (i.carbohydrates / i.quantityG) * 100 : undefined,
      fat100g: i.fat != null ? (i.fat / i.quantityG) * 100 : undefined,
      fiber100g: i.fiber != null ? (i.fiber / i.quantityG) * 100 : undefined,
    })) ?? [],
  );
  const [showPicker, setShowPicker] = useState(false);

  const totals = calcNutrition(items);
  const perServing = {
    energyKcal: totals.energyKcal / (servings || 1),
    proteins: totals.proteins / (servings || 1),
    carbohydrates: totals.carbohydrates / (servings || 1),
    fat: totals.fat / (servings || 1),
    fiber: totals.fiber / (servings || 1),
  };

  const handleAddProduct = (p: Product) => {
    setItems((prev) => [
      ...prev,
      {
        productId: p.id,
        productName: p.name,
        productBrand: p.brand,
        quantityG: 100,
        energyKcal100g: p.energyKcal100g,
        proteins100g: p.proteins100g,
        carbohydrates100g: p.carbohydrates100g,
        fat100g: p.fat100g,
        fiber100g: p.fiber100g,
      },
    ]);
    setShowPicker(false);
  };

  const handleRemoveItem = (idx: number) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleQuantityChange = (idx: number, value: number) => {
    setItems((prev) => prev.map((item, i) => (i === idx ? { ...item, quantityG: value } : item)));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || items.length === 0) return;
    onSave({
      name: name.trim(),
      servings,
      items: items.map((i) => ({ productId: i.productId, quantityG: i.quantityG })),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md border border-primary-200 p-5 mb-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">
        {recipe ? t('recipes.editRecipe') : t('recipes.createRecipe')}
      </h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('recipes.name')}</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('recipes.namePlaceholder')}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('recipes.servings')}</label>
          <input
            type="number"
            value={servings}
            onChange={(e) => setServings(Math.max(1, parseInt(e.target.value) || 1))}
            min={1}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </div>

      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">{t('recipes.items')}</label>
          <button
            type="button"
            onClick={() => setShowPicker(!showPicker)}
            className="text-sm text-primary-600 hover:text-primary-800 font-medium"
          >
            + {t('recipes.addItem')}
          </button>
        </div>

        {showPicker && (
          <div className="mb-3">
            <ProductPicker
              onSelect={handleAddProduct}
              exclude={items.map((i) => i.productId)}
            />
          </div>
        )}

        {items.length === 0 && (
          <p className="text-sm text-gray-400 py-3 text-center">{t('recipes.atLeastOneItem')}</p>
        )}

        <div className="space-y-2">
          {items.map((item, idx) => (
            <div key={item.productId} className="flex items-center gap-3 p-3 bg-gray-50 rounded-md">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{item.productName}</p>
                {item.productBrand && <p className="text-xs text-gray-500">{item.productBrand}</p>}
              </div>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  value={item.quantityG}
                  onChange={(e) => handleQuantityChange(idx, Math.max(1, parseInt(e.target.value) || 1))}
                  min={1}
                  className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-right focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                <span className="text-xs text-gray-500">g</span>
              </div>
              <button
                type="button"
                onClick={() => handleRemoveItem(idx)}
                className="text-gray-400 hover:text-red-500 flex-shrink-0"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      </div>

      {items.length > 0 && (
        <div className="grid grid-cols-2 gap-4 mb-4 p-3 bg-gray-50 rounded-md">
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">{t('recipes.total')}</p>
            <NutritionRow label="Énergie" value={totals.energyKcal} unit=" kcal" />
            <NutritionRow label="Protéines" value={totals.proteins} unit="g" />
            <NutritionRow label="Glucides" value={totals.carbohydrates} unit="g" />
            <NutritionRow label="Lipides" value={totals.fat} unit="g" />
            <NutritionRow label="Fibres" value={totals.fiber} unit="g" />
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">{t('recipes.perServing')} ({servings})</p>
            <NutritionRow label="Énergie" value={perServing.energyKcal} unit=" kcal" />
            <NutritionRow label="Protéines" value={perServing.proteins} unit="g" />
            <NutritionRow label="Glucides" value={perServing.carbohydrates} unit="g" />
            <NutritionRow label="Lipides" value={perServing.fat} unit="g" />
            <NutritionRow label="Fibres" value={perServing.fiber} unit="g" />
          </div>
        </div>
      )}

      <div className="flex gap-3 justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          {t('recipes.cancel')}
        </button>
        <button
          type="submit"
          disabled={!name.trim() || items.length === 0}
          className="px-4 py-2 text-sm font-medium text-white bg-primary-700 hover:bg-primary-800 rounded-md disabled:opacity-50"
        >
          {t('recipes.save')}
        </button>
      </div>
    </form>
  );
}

function RecipesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { page = 1 } = Route.useSearch();
  const limit = 20;

  const [searchQuery, setSearchQuery] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState<Recipe | undefined>();

  const setPage = (newPage: number) => {
    navigate({ to: '/recipes', search: { page: newPage } });
  };

  const { data, isLoading, error } = useRecipes({
    query: searchQuery || undefined,
    page,
    limit,
  });

  const createMutation = useCreateRecipe();
  const updateMutation = useUpdateRecipe();
  const deleteMutation = useDeleteRecipe();

  const recipes = data?.recipes ?? [];
  const totalPages = data ? Math.ceil(data.total / limit) : 0;

  const handleCreate = () => {
    setEditingRecipe(undefined);
    setShowForm(true);
  };

  const handleEdit = (recipe: Recipe) => {
    setEditingRecipe(recipe);
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingRecipe(undefined);
  };

  const handleSave = async (formData: { name: string; servings: number; items: RecipeItemInput[] }) => {
    try {
      if (editingRecipe) {
        await updateMutation.mutateAsync({ id: editingRecipe.id, data: formData });
        toast.success(t('recipes.updated'));
      } else {
        await createMutation.mutateAsync(formData);
        toast.success(t('recipes.created'));
      }
      setShowForm(false);
      setEditingRecipe(undefined);
    } catch {
      toast.error(t('recipes.errorSaving'));
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm(t('recipes.deleteConfirm'))) return;
    try {
      await deleteMutation.mutateAsync(id);
      toast.success(t('recipes.deleted'));
    } catch {
      toast.error(t('recipes.errorLoading'));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('recipes.title')}</h1>
            <p className="mt-1 text-sm text-gray-600">{t('recipes.description')}</p>
          </div>
          {!showForm && (
            <button
              type="button"
              onClick={handleCreate}
              className="px-4 py-2 bg-primary-700 hover:bg-primary-800 text-white text-sm font-medium rounded-md shadow-sm transition-colors"
            >
              {t('recipes.createRecipe')}
            </button>
          )}
        </div>

        {showForm && (
          <RecipeForm
            recipe={editingRecipe}
            onSave={handleSave}
            onCancel={handleCancel}
          />
        )}

        {!showForm && (
          <div className="bg-white rounded-lg shadow-sm p-4 mb-6 border border-gray-200">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
              placeholder={t('recipes.search')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        )}

        {error && (
          <div className="rounded-md bg-red-50 p-4 mb-6">
            <p className="text-sm text-red-700">{t('recipes.errorLoading')}</p>
          </div>
        )}

        {isLoading && (
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 h-20 animate-pulse" />
            ))}
          </div>
        )}

        {!isLoading && recipes.length === 0 && !showForm && (
          <div className="text-center py-12">
            <p className="text-gray-500">{t('recipes.noRecipes')}</p>
            <p className="text-sm text-gray-400 mt-1">{t('recipes.noRecipesDescription')}</p>
          </div>
        )}

        {!isLoading && recipes.length > 0 && (
          <div className="space-y-3">
            {recipes.map((recipe) => (
              <RecipeCard
                key={recipe.id}
                recipe={recipe}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}

        {!isLoading && totalPages > 1 && (
          <div className="mt-8 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Page {page} sur {totalPages}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPage(page - 1)}
                disabled={page <= 1}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.previousPage')}
              </button>
              <button
                type="button"
                onClick={() => setPage(page + 1)}
                disabled={page >= totalPages}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.nextPage')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
